import { create } from 'zustand';
import type { ChatMessage, ConfirmationAction, ActionChip, ToolCallEvent } from '../types/chat';

interface ChatStore {
  // Panel state — single toggle replaces isOpen/isMinimized
  isExpanded: boolean;

  // Conversation
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingMessageId: string | null;
  pendingConfirmation: ConfirmationAction | null;
  unreadCount: number;

  // Confirmation/cancel actions resolved by the user, by action_id
  resolvedConfirmationIds: Set<string>;

  // Action chips for command bar feedback
  actionChips: ActionChip[];

  // Actions
  expand: () => void;
  collapse: () => void;

  addMessage: (message: ChatMessage) => void;
  setStreaming: (streaming: boolean) => void;
  setPendingConfirmation: (confirmation: ConfirmationAction | null) => void;
  clearMessages: () => void;
  resetUnread: () => void;

  // Tool call lifecycle
  updateToolCallStatus: (tool: string, status: ToolCallEvent['status']) => void;

  // Confirmation resolution tracking
  markConfirmationResolved: (actionId: string) => void;

  // History hydration
  hydrateMessages: (messages: ChatMessage[]) => void;
  loadMessages: (messages: ChatMessage[]) => void;

  // Streaming actions
  startStreamingMessage: () => void;
  appendStreamChunk: (delta: string) => void;
  finalizeStreamingMessage: () => void;

  // Action chip actions
  addActionChips: (chips: ActionChip[]) => void;
  clearActionChips: () => void;
}

const MAX_HISTORY = 100;
const UNREAD_MESSAGE_TYPES = new Set<ChatMessage['type']>([
  'assistant',
  'confirmation',
  'action_result',
  'error',
  'rate_limited',
]);

function shouldMarkUnread(message: ChatMessage) {
  return UNREAD_MESSAGE_TYPES.has(message.type) && message.content.trim().length > 0;
}

export const useChatStore = create<ChatStore>((set) => ({
  // Initial state
  isExpanded: false,
  messages: [],
  isStreaming: false,
  streamingMessageId: null,
  pendingConfirmation: null,
  unreadCount: 0,
  resolvedConfirmationIds: new Set<string>(),
  actionChips: [],

  // Panel actions
  expand: () => set({ isExpanded: true, unreadCount: 0 }),
  collapse: () => set({ isExpanded: false }),

  // Message actions
  addMessage: (message) =>
    set((state) => {
      const messages = [...state.messages, message].slice(-MAX_HISTORY);
      const unreadCount =
        !state.isExpanded && shouldMarkUnread(message)
          ? state.unreadCount + 1
          : state.unreadCount;
      return { messages, unreadCount };
    }),

  setStreaming: (streaming) => set({ isStreaming: streaming }),

  setPendingConfirmation: (confirmation) =>
    set({ pendingConfirmation: confirmation }),

  clearMessages: () => set({ messages: [], pendingConfirmation: null, unreadCount: 0 }),

  resetUnread: () => set({ unreadCount: 0 }),

  updateToolCallStatus: (tool, status) =>
    set((state) => {
      // walk from the end: update the most recent bubble for this tool
      const idx = [...state.messages].reverse()
        .findIndex((m) => m.type === 'tool_call' && m.toolCall?.tool === tool);
      if (idx === -1) return {};
      const realIdx = state.messages.length - 1 - idx;
      const messages = state.messages.map((m, i) =>
        i === realIdx ? { ...m, toolCall: { tool, status } } : m);
      return { messages };
    }),

  markConfirmationResolved: (actionId) =>
    set((state) => ({
      resolvedConfirmationIds: new Set(state.resolvedConfirmationIds).add(actionId),
    })),

  hydrateMessages: (messages) =>
    set((state) => (state.messages.length === 0 ? { messages } : {})),

  loadMessages: (messages) => set({
    messages,
    isStreaming: false,
    streamingMessageId: null,
    pendingConfirmation: null,
    actionChips: [],
    unreadCount: 0,
  }),

  // Streaming actions
  startStreamingMessage: () =>
    set((state) => {
      const id = crypto.randomUUID();
      return {
        messages: [...state.messages, { id, type: 'assistant' as const, content: '', timestamp: new Date() }].slice(-MAX_HISTORY),
        streamingMessageId: id,
        isStreaming: true,
      };
    }),

  appendStreamChunk: (delta) =>
    set((state) => {
      if (!state.streamingMessageId) return {};
      const messages = state.messages.map((msg) =>
        msg.id === state.streamingMessageId
          ? { ...msg, content: msg.content + delta }
          : msg
      );
      return { messages };
    }),

  finalizeStreamingMessage: () =>
    set((state) => {
      const completedMessage = state.messages.find((msg) => msg.id === state.streamingMessageId);
      const unreadCount =
        !state.isExpanded && completedMessage && shouldMarkUnread(completedMessage)
          ? state.unreadCount + 1
          : state.unreadCount;

      return { streamingMessageId: null, isStreaming: false, unreadCount };
    }),

  // Action chip actions
  addActionChips: (chips) =>
    set((state) => ({ actionChips: [...state.actionChips, ...chips] })),

  clearActionChips: () => set({ actionChips: [] }),
}));
